import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { ShoppingCart, DollarSign, RefreshCw, Percent, Store, Package, Mail, CheckCircle2, Clock } from "lucide-react";
import { centsToUsd } from "./shared";

function KpiCard({ icon: Icon, label, value, sub, testId, accent }) {
  return (
    <div className="border border-sniper bg-surface p-5" data-testid={testId}>
      <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </span>
      <div className={`font-heading text-3xl mt-2 ${accent || "text-foreground"}`} data-testid={`${testId}-value`}>{value}</div>
      {sub && <p className="text-[10px] font-mono text-muted-foreground mt-1 leading-snug">{sub}</p>}
    </div>
  );
}

const KIND_META = {
  swag: { label: "Swag Store", Icon: Store },
  marketplace: { label: "Marketplace", Icon: Package },
};

function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return String(iso).slice(0, 16); }
}

export default function AbandonedTab({ force }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("abandoned");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/abandoned-checkouts?status=${status}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Couldn't load abandoned checkouts."))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load, force]);

  const sendReminder = async (row) => {
    setBusy(row.abandon_id);
    try {
      const { data: res } = await api.post(`/admin/abandoned-checkouts/${row.abandon_id}/send-reminder`);
      if (res.ok) toast.success(res.environment === "production" ? "Reminder emailed to the buyer." : "Reminder sent (diverted to preview inbox off-production).");
      else toast.error(res.note || "Reminder could not be sent.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reminder could not be sent.");
    } finally {
      setBusy(null);
    }
  };

  const k = data?.kpis || {};
  const byKind = data?.by_kind || {};
  const rows = data?.items || [];
  const env = data?.email_environment;

  return (
    <div data-testid="abandoned-tab">
      <div className="flex items-end justify-between flex-wrap gap-2 border-b border-sniper pb-2 mb-4">
        <div>
          <h2 className="font-heading text-2xl uppercase tracking-tight text-foreground flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-olive-bright" strokeWidth={1.7} /> Abandoned Checkouts
          </h2>
          <p className="text-[11px] font-mono text-muted-foreground mt-1 max-w-2xl leading-snug">
            Checkouts started but never completed within {data?.window_minutes || 60} min — across the Swag store AND marketplace escrow.
            Retries that later converted count as recovered, not lost; duplicate retry attempts are collapsed.
          </p>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground whitespace-nowrap">
          Excludes test + demo
        </span>
      </div>

      {/* Unified KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={ShoppingCart} label="Abandoned" value={k.abandoned_count ?? 0}
          sub="Open · not recovered" testId="abandoned-kpi-count" accent="text-blaze" />
        <KpiCard icon={DollarSign} label="Abandoned Value" value={centsToUsd(k.abandoned_value_cents ?? 0)}
          sub="Cart value left behind" testId="abandoned-kpi-value" />
        <KpiCard icon={RefreshCw} label="Recovered" value={k.recovered_count ?? 0}
          sub={`${centsToUsd(k.recovered_value_cents ?? 0)} recovered`} testId="abandoned-kpi-recovered" accent="text-olive-bright" />
        <KpiCard icon={Percent} label="Recovery Rate" value={`${k.recovery_rate ?? 0}%`}
          sub={`${k.recovered_count ?? 0} of ${k.total_count ?? 0} attempts`} testId="abandoned-kpi-rate" />
      </div>

      {/* Per-flow breakdown */}
      <div className="mt-4 border border-sniper bg-surface divide-y divide-sniper" data-testid="abandoned-bykind">
        <div className="grid grid-cols-4 gap-2 px-3 py-2 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
          <span>Flow</span><span className="text-right">Abandoned</span><span className="text-right">Value</span><span className="text-right">Recovery</span>
        </div>
        {["swag", "marketplace"].map((kind) => {
          const m = byKind[kind] || {};
          const Meta = KIND_META[kind];
          return (
            <div key={kind} className="grid grid-cols-4 gap-2 px-3 py-2.5 font-mono text-xs items-center" data-testid={`abandoned-bykind-${kind}`}>
              <span className="text-secondary-foreground flex items-center gap-1.5">
                <Meta.Icon className="w-3.5 h-3.5 text-olive-bright" /> {Meta.label}
              </span>
              <span className="text-right text-foreground">{m.abandoned_count ?? 0}</span>
              <span className="text-right text-foreground">{centsToUsd(m.abandoned_value_cents ?? 0)}</span>
              <span className="text-right text-foreground">{m.recovery_rate ?? 0}%</span>
            </div>
          );
        })}
      </div>

      {/* Filter + list */}
      <div className="mt-8 flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex border border-sniper bg-surface p-1" data-testid="abandoned-status-filter">
          {[["abandoned", "Open"], ["recovered", "Recovered"], ["all", "All"]].map(([key, lbl]) => (
            <button key={key} onClick={() => setStatus(key)} data-testid={`abandoned-status-filter-${key}`}
              className={`px-4 py-1.5 font-mono uppercase text-[11px] tracking-wide transition-colors ${status === key ? "bg-olive text-white" : "text-secondary-foreground hover:text-white"}`}>
              {lbl}
            </button>
          ))}
        </div>
        {env && env !== "production" && (
          <span className="font-mono text-[10px] uppercase tracking-wide text-[#d3a24e] flex items-center gap-1">
            <Mail className="w-3 h-3" /> Reminders divert to preview inbox off-production
          </span>
        )}
      </div>

      <div className="mt-3 border border-sniper bg-surface" data-testid="abandoned-list">
        {loading ? (
          <p className="p-6 text-sm text-muted-foreground font-body">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground font-body" data-testid="abandoned-empty">
            {status === "abandoned" ? "No open abandoned checkouts. Nice." : "Nothing here yet."}
          </p>
        ) : (
          <div className="divide-y divide-sniper">
            {rows.map((r) => {
              const Meta = KIND_META[r.kind] || KIND_META.marketplace;
              const recovered = r.status === "recovered";
              return (
                <div key={r.abandon_id} className="flex items-center gap-3 px-3 py-3 flex-wrap" data-testid={`abandoned-row-${r.abandon_id}`}>
                  <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide border border-sniper text-secondary-foreground px-1.5 py-0.5">
                    <Meta.Icon className="w-3 h-3" /> {Meta.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-foreground font-body truncate">{r.title || "—"}</div>
                    <div className="font-mono text-[11px] text-muted-foreground truncate">
                      {r.username ? `@${r.username}` : "guest"} · {fmtWhen(r.created_at)}
                      {r.reminder_count > 0 && <span className="text-olive-bright"> · reminded {r.reminder_count}×</span>}
                    </div>
                  </div>
                  <div className="font-heading text-lg text-foreground whitespace-nowrap">{centsToUsd(r.amount_cents)}</div>
                  {recovered ? (
                    <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide border border-olive-bright/50 text-olive-bright px-2 py-1 whitespace-nowrap">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Recovered{r.recovered_via ? ` · ${r.recovered_via}` : ""}
                    </span>
                  ) : (
                    <button onClick={() => sendReminder(r)} disabled={busy === r.abandon_id}
                      data-testid={`abandoned-remind-${r.abandon_id}`}
                      className="inline-flex items-center gap-1.5 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[11px] px-3 py-1.5 transition-colors disabled:opacity-50">
                      {busy === r.abandon_id ? <Clock className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                      {r.reminder_count > 0 ? "Remind again" : "Send reminder"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
